// Admin Dashboard — 4 KPI cards + recent transactions/signups/alerts.
import React, { useEffect, useState } from 'react';
import { adminAxios, useAdminAuth } from './AdminAuthContext';
import {
  TrendingUp, TrendingDown, Users, DollarSign, Percent, Activity, Loader2,
  CheckCircle2, AlertTriangle, ExternalLink, RefreshCw, Mail, Send,
} from 'lucide-react';

const fmtBRL = (n) => `R$ ${Number(n || 0).toFixed(2).replace('.', ',')}`;
const fmtDate = (iso) => {
  if (!iso) return 'n/d';
  try { return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
};

const Delta = ({ pct }) => {
  if (pct == null) return <span className="text-[10px] text-[#94A3B8]">sem histórico</span>;
  const positive = pct >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-bold ${positive ? 'text-[#006030]' : 'text-[#DC2626]'}`}>
      <Icon className="w-3 h-3" /> {positive ? '+' : ''}{pct}%
    </span>
  );
};

const KpiCard = ({ icon: Icon, color, label, value, delta, note, testid }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 shadow-sm" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <div className={`w-9 h-9 rounded-md flex items-center justify-center`} style={{ background: color + '15', color }}>
        <Icon className="w-4 h-4" />
      </div>
      <Delta pct={delta} />
    </div>
    <p className="text-[11px] uppercase tracking-wider text-[#475569]">{label}</p>
    <p className="text-2xl font-black text-[#0F1C2E]">{value}</p>
    {note && <p className="text-[10px] text-[#94A3B8] mt-1">{note}</p>}
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    confirmado: ['bg-[#DCFCE7] text-[#006030]', 'Pago'],
    pago: ['bg-[#DCFCE7] text-[#006030]', 'Pago'],
    pendente: ['bg-[#FEF3C7] text-[#92400E]', 'Pendente'],
    rejeitado: ['bg-[#FEE2E2] text-[#7F1D1D]', 'Falhou'],
    cancelado: ['bg-[#F4F6F8] text-[#475569]', 'Cancelado'],
  };
  const [cls, label] = map[status] || ['bg-[#F4F6F8] text-[#475569]', status || 'n/d'];
  return <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold ${cls}`}>{label}</span>;
};

const AdminDashboard = () => {
  const { token, isSuper } = useAdminAuth();
  const [kpis, setKpis] = useState(null);
  const [recent, setRecent] = useState({ transacoes: [], cadastros: [], alertas: [] });
  const [mpHealth, setMpHealth] = useState(null);
  const [mpLoading, setMpLoading] = useState(false);
  const [emailHealth, setEmailHealth] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const loadMpHealth = async () => {
    if (!isSuper) return;
    setMpLoading(true);
    try {
      const r = await adminAxios(token).get('/api/admin/mp/account-health');
      setMpHealth(r.data);
    } catch (e) {
      setMpHealth({ pix_status: 'desconhecido', mensagem: e?.response?.data?.detail || 'Falha ao consultar Mercado Pago.' });
    } finally {
      setMpLoading(false);
    }
  };

  const runEmailHealth = async () => {
    if (!isSuper) return;
    setEmailLoading(true);
    try {
      const params = emailTo.trim() ? `?to=${encodeURIComponent(emailTo.trim())}` : '';
      const r = await adminAxios(token).get(`/api/admin/health/email${params}`);
      setEmailHealth(r.data);
    } catch (e) {
      setEmailHealth({
        overall: 'critical',
        message: e?.response?.data?.detail || 'Falha ao testar Resend.',
      });
    } finally {
      setEmailLoading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const ax = adminAxios(token);
        const [k, r] = await Promise.all([
          ax.get('/api/admin/dashboard/kpis'),
          ax.get('/api/admin/dashboard/recent'),
        ]);
        setKpis(k.data);
        setRecent(r.data);
      } catch (e) { setErr(e?.response?.data?.detail || 'Falha ao carregar dashboard'); }
      finally { setLoading(false); }
    };
    load();
    if (isSuper) loadMpHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isSuper]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#1E3A5F]" /></div>;
  if (err) return <p className="text-sm text-[#DC2626]">{err}</p>;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl font-black text-[#0F1C2E]">Dashboard executivo</h1>
        <p className="text-xs text-[#475569]">Visão consolidada do Tô de Olho · atualizado em {fmtDate(kpis?.computed_at)}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          icon={DollarSign} color="#1E3A5F" label="MRR (Receita Recorrente)"
          value={kpis?.mrr?.label || 'R$ 0,00'}
          delta={kpis?.mrr?.delta_pct} note={kpis?.mrr?.note}
          testid="kpi-mrr"
        />
        <KpiCard
          icon={Users} color="#006030" label="Ativos últimos 7 dias"
          value={kpis?.active_7d?.value ?? 0}
          delta={kpis?.active_7d?.delta_pct}
          testid="kpi-active"
        />
        <KpiCard
          icon={Percent} color="#F59E0B" label="Conversão Free → Pro"
          value={kpis?.conversion?.label || '0%'}
          delta={kpis?.conversion?.delta_pct}
          testid="kpi-conversion"
        />
        <KpiCard
          icon={Activity} color="#DC2626" label="Churn rate (30d)"
          value={kpis?.churn?.label || '0%'}
          delta={kpis?.churn?.delta_pct} note={kpis?.churn?.note}
          testid="kpi-churn"
        />
      </div>

      {isSuper && (
        <div
          className={`border rounded-lg p-4 ${
            mpHealth?.pix_status === 'liberado'
              ? 'bg-[#F0FDF4] border-[#BBF7D0]'
              : mpHealth?.pix_status === 'bloqueado'
                ? 'bg-[#FEF3C7] border-[#FDE68A]'
                : 'bg-white border-[#E2E8F0]'
          }`}
          data-testid="mp-account-health"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${
                mpHealth?.pix_status === 'liberado'
                  ? 'bg-[#DCFCE7] text-[#006030]'
                  : 'bg-[#FEE2E2] text-[#7F1D1D]'
              }`}>
                {mpHealth?.pix_status === 'liberado'
                  ? <CheckCircle2 className="w-5 h-5" />
                  : <AlertTriangle className="w-5 h-5" />}
              </div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-[#475569]">
                  Mercado Pago — saúde da conta coletora
                </p>
                <p className="text-sm font-bold text-[#0F1C2E]" data-testid="mp-health-message">
                  {mpLoading ? 'Consultando Mercado Pago…' : (mpHealth?.mensagem || 'Aguardando dados...')}
                </p>
                {mpHealth?.conta && (
                  <p className="text-[11px] text-[#475569] mt-0.5 truncate">
                    {mpHealth.conta.nome} · {mpHealth.conta.tipo_doc} {mpHealth.conta.cnpj_cpf} · {mpHealth.conta.email}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={loadMpHealth}
                disabled={mpLoading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold bg-white border border-[#E2E8F0] text-[#1E3A5F] hover:bg-[#F8FAFC] disabled:opacity-50"
                data-testid="mp-health-refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${mpLoading ? 'animate-spin' : ''}`} /> Atualizar
              </button>
              {mpHealth?.pix_status === 'bloqueado' && (
                <a
                  href={mpHealth?.next_step_url || 'https://www.mercadopago.com.br/settings/account/personal-data'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold bg-[#1E3A5F] hover:bg-[#0F1C2E] text-white"
                  data-testid="mp-health-fix-link"
                >
                  Corrigir no MP <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          </div>

          {mpHealth && mpHealth.pix_status !== 'token_invalido' && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
              <div className="bg-white/70 rounded p-3" data-testid="mp-billing-allow">
                <p className="text-[10px] uppercase tracking-wider text-[#475569]">billing.allow</p>
                <p className={`text-sm font-bold ${mpHealth.billing_allow ? 'text-[#006030]' : 'text-[#DC2626]'}`}>
                  {String(mpHealth.billing_allow)}
                </p>
                {mpHealth.billing_codes?.length > 0 && (
                  <p className="text-[10px] text-[#7F1D1D] mt-0.5">
                    códigos: {mpHealth.billing_codes.join(', ')}
                  </p>
                )}
              </div>
              <div className="bg-white/70 rounded p-3" data-testid="mp-list-allow">
                <p className="text-[10px] uppercase tracking-wider text-[#475569]">list.allow</p>
                <p className={`text-sm font-bold ${mpHealth.list_allow ? 'text-[#006030]' : 'text-[#DC2626]'}`}>
                  {String(mpHealth.list_allow)}
                </p>
                {mpHealth.list_codes?.length > 0 && (
                  <p className="text-[10px] text-[#7F1D1D] mt-0.5">
                    códigos: {mpHealth.list_codes.join(', ')}
                  </p>
                )}
              </div>
              <div className="bg-white/70 rounded p-3" data-testid="mp-address">
                <p className="text-[10px] uppercase tracking-wider text-[#475569]">Endereço</p>
                {mpHealth.address?.completo ? (
                  <>
                    <p className="text-xs font-bold text-[#006030]">Completo ✅</p>
                    <p className="text-[10px] text-[#475569] mt-0.5 truncate">
                      {mpHealth.address.logradouro}, {mpHealth.address.cidade}/{mpHealth.address.estado}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-xs font-bold text-[#DC2626]">Pendente</p>
                    <p className="text-[10px] text-[#7F1D1D] mt-0.5">Logradouro, cidade, UF e CEP nulos.</p>
                  </>
                )}
              </div>
              <div className="bg-white/70 rounded p-3" data-testid="mp-phone">
                <p className="text-[10px] uppercase tracking-wider text-[#475569]">Telefone verificado</p>
                <p className={`text-sm font-bold ${mpHealth.phone?.verified ? 'text-[#006030]' : 'text-[#DC2626]'}`}>
                  {mpHealth.phone?.verified ? 'Sim ✅' : 'Não'}
                </p>
                {!mpHealth.phone?.verified && (
                  <p className="text-[10px] text-[#7F1D1D] mt-0.5">SMS de verificação pendente.</p>
                )}
              </div>
            </div>
          )}

          {mpHealth?.checked_at && (
            <p className="text-[10px] text-[#94A3B8] mt-2">
              Consultado em {fmtDate(mpHealth.checked_at)}
            </p>
          )}
        </div>
      )}

      {isSuper && (
        <div
          className={`border rounded-lg p-4 ${
            emailHealth?.overall === 'ok'
              ? 'bg-[#F0FDF4] border-[#BBF7D0]'
              : emailHealth?.overall === 'warning'
                ? 'bg-[#FEF3C7] border-[#FDE68A]'
                : emailHealth?.overall === 'critical'
                  ? 'bg-[#FEE2E2] border-[#FECACA]'
                  : 'bg-white border-[#E2E8F0]'
          }`}
          data-testid="email-health-card"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <div
                className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${
                  emailHealth?.overall === 'ok'
                    ? 'bg-[#DCFCE7] text-[#006030]'
                    : emailHealth?.overall === 'warning'
                      ? 'bg-[#FEF3C7] text-[#92400E]'
                      : emailHealth?.overall === 'critical'
                        ? 'bg-[#FEE2E2] text-[#7F1D1D]'
                        : 'bg-[#F4F6F8] text-[#475569]'
                }`}
              >
                <Mail className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-[#475569]">
                  E-mail (Resend) — diagnóstico
                </p>
                <p className="text-sm font-bold text-[#0F1C2E]" data-testid="email-health-message">
                  {emailLoading
                    ? 'Testando envio...'
                    : (emailHealth?.message || 'Clique em "Testar agora" para disparar um envio real.')}
                </p>
                {emailHealth?.config && (
                  <p className="text-[11px] text-[#475569] mt-0.5 truncate">
                    Key {emailHealth.config.resend_api_key_prefix} ·
                    Remetente {emailHealth.config.resend_from_email || '(sem)'}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="destino (padrão: seu e-mail)"
                className="px-2 py-1.5 border border-[#E2E8F0] rounded text-xs w-56"
                data-testid="email-health-to"
              />
              <button
                onClick={runEmailHealth}
                disabled={emailLoading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold bg-[#1E3A5F] hover:bg-[#0F1C2E] text-white disabled:opacity-60"
                data-testid="email-health-run"
              >
                {emailLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Testar agora
              </button>
            </div>
          </div>

          {emailHealth?.domains && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-4">
              <div className="bg-white/70 rounded p-3" data-testid="email-domains">
                <p className="text-[10px] uppercase tracking-wider text-[#475569]">Domínios cadastrados</p>
                <p className="text-sm font-bold text-[#0F1C2E]">{emailHealth.domains.count ?? 0}</p>
                {(emailHealth.domains.items || []).map((d, i) => (
                  <p
                    key={i}
                    className={`text-[10px] mt-0.5 ${
                      (d.status || '').toLowerCase() === 'verified' ? 'text-[#006030]' : 'text-[#7F1D1D]'
                    }`}
                  >
                    {d.name} · {d.status}
                  </p>
                ))}
                {emailHealth.domains.count === 0 && (
                  <p className="text-[10px] text-[#7F1D1D] mt-0.5">Nenhum domínio na conta Resend.</p>
                )}
              </div>
              <div className="bg-white/70 rounded p-3" data-testid="email-test-primary">
                <p className="text-[10px] uppercase tracking-wider text-[#475569]">Envio principal</p>
                <p
                  className={`text-sm font-bold ${
                    [200, 202].includes(emailHealth.test_send?.primary_status)
                      ? 'text-[#006030]'
                      : 'text-[#DC2626]'
                  }`}
                >
                  HTTP {emailHealth.test_send?.primary_status ?? '—'}
                </p>
                {emailHealth.test_send?.primary_body?.message && (
                  <p className="text-[10px] text-[#7F1D1D] mt-0.5 line-clamp-3">
                    {emailHealth.test_send.primary_body.message}
                  </p>
                )}
              </div>
              <div className="bg-white/70 rounded p-3" data-testid="email-test-fallback">
                <p className="text-[10px] uppercase tracking-wider text-[#475569]">Fallback sandbox</p>
                {emailHealth.test_send?.sandbox_used ? (
                  <>
                    <p
                      className={`text-sm font-bold ${
                        [200, 202].includes(emailHealth.test_send.sandbox_status)
                          ? 'text-[#006030]'
                          : 'text-[#DC2626]'
                      }`}
                    >
                      HTTP {emailHealth.test_send.sandbox_status}
                    </p>
                    {emailHealth.test_send.message_id && (
                      <p className="text-[10px] text-[#475569] mt-0.5 truncate">id {emailHealth.test_send.message_id}</p>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-[#94A3B8]">Não acionado</p>
                )}
              </div>
            </div>
          )}

          {emailHealth?.checked_at && (
            <p className="text-[10px] text-[#94A3B8] mt-2">
              Consultado em {fmtDate(emailHealth.checked_at)}
            </p>
          )}

          {emailHealth?.overall !== 'ok' && (
            <a
              href="https://resend.com/domains"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-3 text-xs font-bold text-[#1E3A5F] hover:underline"
              data-testid="email-health-fix-link"
            >
              Verificar domínio no Resend <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid="recent-transactions">
          <h3 className="text-sm font-bold text-[#0F1C2E] mb-3">Últimas transações</h3>
          {recent.transacoes.length === 0 ? (
            <p className="text-xs text-[#94A3B8]">Sem transações ainda. Aparecerão aqui assim que o primeiro PIX for confirmado.</p>
          ) : (
            <ul className="space-y-2">
              {recent.transacoes.map((t, i) => (
                <li key={i} className="flex items-center justify-between text-xs">
                  <div>
                    <p className="font-medium text-[#0F1C2E] truncate max-w-[180px]">{t.usuario_nome || t.usuario_email || 'Anônimo'}</p>
                    <p className="text-[10px] text-[#94A3B8]">{fmtDate(t.criado_em)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-[#0F1C2E]">{fmtBRL(t.valor)}</p>
                    <StatusBadge status={t.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid="recent-signups">
          <h3 className="text-sm font-bold text-[#0F1C2E] mb-3">Últimos cadastros</h3>
          {recent.cadastros.length === 0 ? (
            <p className="text-xs text-[#94A3B8]">Sem cadastros recentes.</p>
          ) : (
            <ul className="space-y-2">
              {recent.cadastros.map((u, i) => (
                <li key={i} className="text-xs">
                  <p className="font-medium text-[#0F1C2E] truncate">{u.nome}</p>
                  <p className="text-[10px] text-[#94A3B8] truncate">{u.email} · {fmtDate(u.criado_em)}</p>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid="recent-alerts">
          <h3 className="text-sm font-bold text-[#0F1C2E] mb-3">Alertas recentes</h3>
          {recent.alertas.length === 0 ? (
            <p className="text-xs text-[#94A3B8]">Nenhum alerta no momento. O sistema avisa quando algo sair do padrão.</p>
          ) : (
            <ul className="space-y-2">
              {recent.alertas.map((a, i) => (
                <li key={i} className="text-xs">
                  <p className="font-medium text-[#0F1C2E]">{a.titulo}</p>
                  <p className="text-[10px] text-[#94A3B8]">{fmtDate(a.ts)}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
